import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { filesApi } from "@/lib/humhub-api/files";

interface UseFileByGuidReturn {
  fileBlobUrl: string | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

export const useFileByGuid = (
  guid: string | null | undefined,
  enabled = true,
): UseFileByGuidReturn => {
  // React Queryでファイル取得
  const {
    data: blob,
    isLoading,
    error: queryError,
    refetch: queryRefetch,
  } = useQuery({
    queryKey: ["fileBlob", guid],
    queryFn: async () => {
      if (!guid) {
        throw new Error("File GUID is null or undefined");
      }

      const blob = await filesApi.getFileByGuid(guid);

      if (blob.size === 0) {
        throw new Error("Received empty blob");
      }

      return blob;
    },
    enabled: enabled && !!guid, // guidがある場合のみクエリ実行
    staleTime: 10 * 60 * 1000, // ファイルは10分間キャッシュ
    gcTime: 30 * 60 * 1000, // 30分間メモリに保持
    // Blobオブジェクトは参照で比較するため、常に新しいものとして扱う
    structuralSharing: false,
  });

  // BlobからURLを生成（メモ化してURLの無駄な生成を防止）
  const fileBlobUrl = useMemo(() => {
    if (!blob) return null;

    const url = URL.createObjectURL(blob);

    // クリーンアップ関数を返す（コンポーネントアンマウント時に自動実行）
    return url;
  }, [blob]);

  // クリーンアップ処理（コンポーネントアンマウント時）
  useEffect(() => {
    return () => {
      if (fileBlobUrl) {
        URL.revokeObjectURL(fileBlobUrl);
      }
    };
  }, [fileBlobUrl]);

  const refetch = () => {
    // 既存のBlobURLを削除してからリフェッチ
    if (fileBlobUrl) {
      URL.revokeObjectURL(fileBlobUrl);
    }
    queryRefetch();
  };

  return {
    fileBlobUrl,
    isLoading,
    error: queryError instanceof Error ? queryError : null,
    refetch,
  };
};
